import { ChangeEvent, FC, MouseEvent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import { CustomAlert, CustomButton, CustomInput, Text } from '../../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { EditSvg } from '../../../../../shared/Assets/svgIcons';
import { ButtonWrapper } from '../../../../../components/StyledComponents';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { SkillAreaProperties, SkillProperties } from '../../../../../redux/API/ClientAPIHelpers/skillAreaProperties';
import { getSkillAreas, updateSkillAreas } from '../../../../../redux/State/clientSlice/skillAreaSlice';

const RenameDialogContent = ({ item, onClose }: { item: SkillProperties; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const [name, setName] = useState<string>(item.name);

	const isUpdating = useSelector((state) => state.skillArea.isUpdating);

	const refreshSkillAreas = () => {
		const { clientId } = item;

		if (!clientId) return;
		dispatch(getSkillAreas({ clientId, funderId: item.funderId }));
	};

	const updateSkillAreaHandler = () => {
		const { clientId, id } = item;

		if (!clientId || !id) return;
		const data: SkillAreaProperties = {
			clientId,
			name,
		};
		dispatch(updateSkillAreas({ data, id }))
			.then(unwrapResult)
			.then(() => {
				onClose();
				refreshSkillAreas();
			})
			.catch(errorNormalizer);
	};

	const nameHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setName(value);
	};

	return (
		<div>
			<CustomInput value={name} onChange={nameHandler} className="marginBottom16" />
			<ButtonWrapper isInModal>
				<CustomButton title="Save" onClick={updateSkillAreaHandler} loading={isUpdating} />
				<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} disabled={isUpdating} />
			</ButtonWrapper>
		</div>
	);
};

const Rename = ({ item }: { item: SkillProperties }) => {
	const [open, setOpen] = useState<boolean>(false);

	const onEditClick = (event: MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
		setOpen(true);
	};

	// Stable component reference so CustomAlert (which renders <Content onClose={…} />)
	// does not unmount/remount the dialog body on parent re-renders.
	const DialogContent: FC<{ onClose: () => void }> = useCallback(
		({ onClose }) => <RenameDialogContent item={item} onClose={onClose} />,
		[item],
	);

	return (
		<>
			<div className="actionRow" onClick={onEditClick}>
				<div className="actionIcon">
					<EditSvg color="#62615F" />
				</div>
				<div className="actionContent">
					<Text title="Rename" type="psm" fontWeight="medium" />
				</div>
			</div>
			<CustomAlert
				open={open}
				onClose={() => setOpen(false)}
				title="Edit skill acquisition name"
				Content={DialogContent}
			/>
		</>
	);
};

export default Rename;
